import React from 'react';
import { Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import { SmartToy as SmartToyIcon } from '@mui/icons-material';

const RobotPipeline = ({ bot }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Make component more responsive
  const size = isMobile ? 'small' : isTablet ? 'medium' : 'large';
  
  // Sizing based on responsive needs - increase height by 15%
  const dimensions = {
    small: {
      width: '100%',
      height: 253, // Increased from 220 by 15%
      robotSize: 40,
      iconSize: 20,
      fontSize: '0.75rem',
      pipeHeight: 8
    },
    medium: {
      width: '100%',
      height: 322, // Increased from 280 by 15%
      robotSize: 50,
      iconSize: 24,
      fontSize: '0.85rem',
      pipeHeight: 10
    },
    large: {
      width: '100%',
      height: 402, // Increased from 350 by 15%
      robotSize: 60,
      iconSize: 30,
      fontSize: '1rem',
      pipeHeight: 12
    }
  };
  
  const { width, height, robotSize, iconSize, fontSize, pipeHeight } = dimensions[size];

  return (
    <Paper
      elevation={4}
      sx={{
        width,
        height,
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: 'rgba(30, 35, 45, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        animation: 'fadeInUp 1s ease-out forwards',
        my: { xs: 2, md: 3 }, // Add more vertical margin to account for taller height
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
        }}
      >
        {/* Background circuit patterns */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.07,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.828-1.415 1.415L51.8 0h2.827zM5.373 0l-.83.828L5.96 2.243 8.2 0H5.374zM48.97 0l3.657 3.657-1.414 1.414L46.143 0h2.828zM11.03 0L7.372 3.657 8.787 5.07 13.857 0H11.03zm32.284 0L49.8 6.485 48.384 7.9l-7.9-7.9h2.83zM16.686 0L10.2 6.485 11.616 7.9l7.9-7.9h-2.83z' fill='%23FFFFFF' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Title */}
        <Typography
          variant={isMobile ? "body1" : "h6"}
          sx={{ 
            color: 'white', 
            textAlign: 'center', 
            py: 2,
            fontWeight: 'medium',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          AI Chatbot Processing Pipeline
        </Typography>

        {/* Conveyor belt */}
        <Box
          sx={{
            position: 'absolute',
            left: '10%',
            right: '10%',
            height: pipeHeight,
            backgroundColor: '#3a3a3a',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.3) 10%, transparent 10%, transparent 20%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.3) 30%, transparent 30%, transparent 40%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.3) 50%, transparent 50%, transparent 60%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.3) 70%, transparent 70%, transparent 80%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0.3) 90%, transparent 90%, transparent 100%)',
            backgroundSize: '800px 100%',
            animation: 'moveBelt 20s linear infinite',
            boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.3)',
            zIndex: 1,
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px)',
              backgroundSize: '8px 8px',
              pointerEvents: 'none',
            },
          }}
        />

        {/* User input */}
        <Box
          sx={{
            position: 'absolute',
            left: '5%',
            top: '50%',
            transform: 'translateY(-130%)',
            textAlign: 'center',
            zIndex: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontWeight: 'medium',
              fontSize,
              mb: 1
            }}
          >
            User Input
          </Typography>
          <Box
            sx={{
              width: robotSize * 1.8,
              padding: '8px 12px',
              borderRadius: '12px',
              backgroundColor: 'white',
              color: '#222',
              fontSize,
              position: 'relative',
              animation: 'chatBubble 5s infinite',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: '14px',
                height: '14px',
                backgroundColor: 'white',
              },
            }}
          >
            Hello AI!
          </Box>
          <Box
            sx={{
              width: robotSize,
              height: robotSize,
              borderRadius: '50%',
              backgroundColor: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 1.5,
              mx: 'auto',
              animation: 'subtle-bounce 3s infinite ease-in-out',
            }}
          >
            <Typography 
              sx={{ 
                color: 'white', 
                fontWeight: 'bold',
                fontSize
              }}
            >
              You
            </Typography>
          </Box>
        </Box>

        {/* Moving robot */}
        <Box
          sx={{
            position: 'absolute',
            left: '-80px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            animation: 'moveRobotOnBelt 10s infinite linear',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: robotSize * 1.2,
              height: robotSize * 1.2,
              borderRadius: '50%',
              backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 20px ${bot?.customizations?.primaryColor || theme.palette.primary.main}60`,
              animation: 'pulseRobot 2s infinite ease-in-out',
              border: '3px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <SmartToyIcon sx={{ fontSize: iconSize * 1.5, color: 'white' }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ 
              color: 'white', 
              mt: 1, 
              fontWeight: 'medium',
              fontSize
            }}
          >
            {bot?.name || 'AI Bot'}
          </Typography>
        </Box>

        {/* Processing stages */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(50%)',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-around',
            px: 4,
            zIndex: 3,
          }}
        >
          {['NLP Processing', 'Knowledge Base', 'Response Generation'].map(
            (stage, index) => (
              <Box
                key={stage}
                sx={{
                  textAlign: 'center',
                  opacity: 0,
                  animation: `fadeInUp 0.5s forwards ${0.5 + index * 0.2}s`,
                }}
              >
                <Box
                  sx={{
                    width: robotSize * 0.8,
                    height: robotSize * 0.8,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    mx: 'auto',
                    position: 'relative',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage:
                        'radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      opacity: 0.5,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -100,
                      left: -100,
                      width: 50,
                      height: 200,
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transform: 'rotate(45deg)',
                      animation: 'flowingLight 3s infinite',
                      animationDelay: `${index * 0.7}s`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '50%',
                      height: '50%',
                      backgroundImage: [
                        "url('https://img.icons8.com/ios-filled/50/ffffff/brain.png')",
                        "url('https://img.icons8.com/ios-filled/50/ffffff/database.png')",
                        "url('https://img.icons8.com/ios-filled/50/ffffff/reply-all-arrow.png')",
                      ][index],
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.8,
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)', 
                    fontWeight: 'medium', 
                    fontSize
                  }}
                >
                  {stage}
                </Typography>
              </Box>
            )
          )}
        </Box>

        {/* AI output */}
        <Box
          sx={{
            position: 'absolute',
            right: '5%',
            top: '50%',
            transform: 'translateY(-130%)',
            textAlign: 'center',
            zIndex: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontWeight: 'medium',
              fontSize,
              mb: 1
            }}
          >
            AI Response
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              width: robotSize,
              height: robotSize,
              borderRadius: '50%',
              backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              animation: 'subtle-bounce 3s infinite ease-in-out 0.5s',
            }}
          >
            <SmartToyIcon sx={{ fontSize: iconSize, color: 'white' }} />
          </Box>
          <Box
            sx={{
              width: robotSize * 1.8,
              padding: '8px 12px',
              borderRadius: '12px',
              backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
              color: 'white',
              fontSize,
              mt: 1.5,
              position: 'relative',
              animation: 'chatBubble 5s infinite 0.5s',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: '14px',
                height: '14px',
                backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
              },
            }}
          >
            Hello! How can I help?
          </Box>
        </Box>

        {/* Data flow animations */}
        <Box
          sx={{
            position: 'absolute',
            top: '70%',
            left: '30%',
            width: '40%',
            height: '15%',
            opacity: 0.7,
          }}
        >
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.7)',
                animation: `flowInPipe 3s infinite`,
                animationDelay: `${i * 0.6}s`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default RobotPipeline;
